import { useConfigStore, DEFAULT_CONFIG } from '../../stores/configStore';
import type { StoryType } from '../../api/types';

function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  defaultValue,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  defaultValue: number;
}) {
  const isModified = Math.abs(value - defaultValue) > 0.001;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-sm text-gray-600">{label}</label>
        <div className="flex items-center gap-2">
          <span className={`font-mono text-sm ${isModified ? 'text-pebbl-600 font-medium' : 'text-gray-700'}`}>
            {value.toFixed(2)}
          </span>
          {isModified && (
            <span className="text-xs text-gray-400">({defaultValue})</span>
          )}
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-pebbl-600"
      />
    </div>
  );
}

function ProximityRow({
  label,
  storyType,
  value,
  defaultValue,
}: {
  label: string;
  storyType: StoryType;
  value: number | null;
  defaultValue: number | null;
}) {
  const setProximityRadius = useConfigStore((s) => s.setProximityRadius);
  const enabled = value !== null;
  const isModified = value !== defaultValue;
  const sliderValue = value ?? defaultValue ?? 200;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-sm text-gray-600">{label}</label>
        <div className="flex items-center gap-2">
          <span className={`font-mono text-sm ${isModified ? 'text-pebbl-600 font-medium' : 'text-gray-700'}`}>
            {enabled ? `${value}m` : 'off'}
          </span>
          <label className="flex items-center gap-1 text-xs text-gray-500">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) =>
                setProximityRadius(
                  storyType,
                  e.target.checked ? defaultValue ?? 200 : null,
                )
              }
            />
            on
          </label>
        </div>
      </div>
      <input
        type="range"
        min={50}
        max={1000}
        step={50}
        value={sliderValue}
        disabled={!enabled}
        onChange={(e) => setProximityRadius(storyType, parseInt(e.target.value, 10))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-pebbl-600 disabled:opacity-50"
      />
    </div>
  );
}

export default function ConfigPanel() {
  const { config, setGate, setFusionAlpha, setRerankTopM, setBypassFloor } = useConfigStore();

  return (
    <div className="card space-y-6">
      <h3 className="font-semibold text-gray-900">Configuration</h3>

      {/* Gates */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Gate Thresholds</h4>
        <div className="space-y-4">
          <Slider
            label="Min Overall Score"
            value={config.minScore}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => setGate('minScore', v)}
            defaultValue={DEFAULT_CONFIG.minScore}
          />
          <Slider
            label="Min Event"
            value={config.minEvent}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => setGate('minEvent', v)}
            defaultValue={DEFAULT_CONFIG.minEvent}
          />
          <Slider
            label="Min Location"
            value={config.minLocation}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => setGate('minLocation', v)}
            defaultValue={DEFAULT_CONFIG.minLocation}
          />
          <Slider
            label="Min Time"
            value={config.minTime}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => setGate('minTime', v)}
            defaultValue={DEFAULT_CONFIG.minTime}
          />
        </div>
      </div>

      {/* Bypass settings */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Bypass Settings</h4>
        <div className="space-y-4">
          <Slider
            label="Bypass Floor"
            value={config.bypassFloor}
            min={0}
            max={1}
            step={0.05}
            onChange={setBypassFloor}
            defaultValue={DEFAULT_CONFIG.bypassFloor}
          />
          <ProximityRow
            label="Proximity Radius — Stranger"
            storyType="stranger"
            value={config.proximityRadiusMeters.stranger}
            defaultValue={DEFAULT_CONFIG.proximityRadiusMeters.stranger}
          />
          <ProximityRow
            label="Proximity Radius — Crush"
            storyType="crush"
            value={config.proximityRadiusMeters.crush}
            defaultValue={DEFAULT_CONFIG.proximityRadiusMeters.crush}
          />
          <ProximityRow
            label="Proximity Radius — Ex"
            storyType="ex"
            value={config.proximityRadiusMeters.ex}
            defaultValue={DEFAULT_CONFIG.proximityRadiusMeters.ex}
          />
        </div>
      </div>

      {/* Dimension weights — per story type (read-only; live editor deferred,
          backend config-override schema does not carry dimensionWeights) */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          Dimension Weights <span className="text-xs font-normal text-gray-400">(per type · read-only)</span>
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-gray-500 text-left">
                <th className="font-medium pr-2">type</th>
                <th className="font-medium px-1 text-right">event</th>
                <th className="font-medium px-1 text-right">loc</th>
                <th className="font-medium px-1 text-right">app</th>
                <th className="font-medium px-1 text-right">time</th>
                <th className="font-medium px-1 text-right">ident</th>
                <th className="font-medium px-1 text-right">mirror</th>
                <th className="font-medium px-1 text-right">uniq</th>
                <th className="font-medium pl-2 text-right">Σ</th>
              </tr>
            </thead>
            <tbody>
              {(['stranger', 'crush', 'ex'] as const).map((t) => {
                const w = config.dimensionWeights[t];
                const sum = Object.values(w).reduce((a, b) => a + b, 0);
                return (
                  <tr key={t} className="text-gray-700">
                    <td className="pr-2 text-gray-500">{t}</td>
                    <td className="px-1 text-right">{w.event.toFixed(2)}</td>
                    <td className="px-1 text-right">{w.location.toFixed(2)}</td>
                    <td className="px-1 text-right">{w.appearance.toFixed(2)}</td>
                    <td className="px-1 text-right">{w.time.toFixed(2)}</td>
                    <td className="px-1 text-right">{w.identity.toFixed(2)}</td>
                    <td className="px-1 text-right">{w.mirror.toFixed(2)}</td>
                    <td className="px-1 text-right">{w.uniquenessBonus.toFixed(2)}</td>
                    <td className="pl-2 text-right text-gray-400">{sum.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fusion */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Fusion Settings</h4>
        <div className="space-y-4">
          <Slider
            label="Fusion Alpha (det vs LLM)"
            value={config.fusionAlpha}
            min={0}
            max={1}
            step={0.05}
            onChange={setFusionAlpha}
            defaultValue={DEFAULT_CONFIG.fusionAlpha}
          />
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-600">Rerank Top M</label>
              <span className="font-mono text-sm text-gray-700">{config.rerankTopM}</span>
            </div>
            <input
              type="range"
              min={1}
              max={30}
              step={1}
              value={config.rerankTopM}
              onChange={(e) => setRerankTopM(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-pebbl-600"
            />
          </div>
        </div>
      </div>

      {/* Per-type weight sums are shown in the Σ column of the table above. */}
    </div>
  );
}
